import {
  AfterViewInit,
  Component,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  inject,
  ChangeDetectionStrategy
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Router } from '@angular/router';
import { FormControl, Validators } from '@angular/forms';
import {
  ApplicationVerifier,
  MultiFactorInfo,
  MultiFactorResolver,
  PhoneAuthProvider,
  PhoneMultiFactorGenerator,
  RecaptchaVerifier,
  TotpMultiFactorGenerator,
} from 'firebase/auth';
import { AuthService } from '@core/services/auth.service';
import { FirebaseInjectionHelperService } from '@core/services/firebase-injection-helper.service';
import { LoggingService } from '@core/services/logging.service';

/**
 * MfaChallengeComponent
 *
 * Second-factor step of the sign-in flow. AuthService lands users here after
 * a first-factor sign-in raised `auth/multi-factor-auth-required`, having
 * stashed the Firebase MultiFactorResolver in memory. This page:
 * - Redirects to /login when no resolver is stashed (e.g. page refresh).
 * - Lists the account's enrolled factors (resolver.hints) for selection.
 * - Phone factors: sends an SMS via an invisible reCAPTCHA + the resolver
 *   session, then resolves sign-in with the received code.
 * - TOTP factors: resolves sign-in directly with the authenticator code.
 * - On success clears the resolver and hands off to /logging-in (the normal
 *   post-login hydration flow).
 *
 * SSR-safe: all Firebase/DOM work is guarded with isPlatformBrowser.
 */
@Component({
  selector: 'app-mfa-challenge',
  templateUrl: './mfa-challenge.component.html',
  styleUrl: './mfa-challenge.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class MfaChallengeComponent implements OnInit, AfterViewInit, OnDestroy {
  private authService = inject(AuthService);
  private firebaseHelper = inject(FirebaseInjectionHelperService);
  private loggingService = inject(LoggingService);
  private router = inject(Router);
  private platformId = inject(PLATFORM_ID);

  /** Factor ID constant for phone hints (template comparisons). */
  readonly phoneFactorId = PhoneMultiFactorGenerator.FACTOR_ID;
  /** Factor ID constant for TOTP hints (template comparisons). */
  readonly totpFactorId = TotpMultiFactorGenerator.FACTOR_ID;

  /** Enrolled second factors available to complete this sign-in. */
  hints: MultiFactorInfo[] = [];
  /** Factor the user chose to verify with (auto-selected when only one). */
  selectedHint: MultiFactorInfo | null = null;
  /** True once the SMS verification code has been sent for a phone hint. */
  smsSent = false;
  /** True while a Firebase call is in flight. */
  loading = false;
  /** Inline error (wrong code, expired code, send failures). */
  errorMessage: string | null = null;
  /** Positive feedback (e.g. "code sent"). */
  infoMessage: string | null = null;

  /** 6-digit verification code input (SMS or authenticator app). */
  codeControl = new FormControl<string>('', {
    nonNullable: true,
    validators: [
      Validators.required,
      Validators.pattern(/^\d{6}$/),
    ],
  });

  private resolver: MultiFactorResolver | null = null;
  private verificationId = '';
  private recaptchaVerifier: ApplicationVerifier | null = null;

  ngOnInit(): void {
    // SSR safety: the resolver and Firebase Auth exist only in the browser.
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    this.resolver = this.authService.getMfaResolver();
    if (!this.resolver) {
      // No in-progress multi-factor sign-in (e.g. direct visit or refresh).
      void this.router.navigate(['/login'], { replaceUrl: true });
      return;
    }

    this.hints = this.resolver.hints;
    if (this.hints.length === 1) {
      this.selectedHint = this.hints[0];
    }
  }

  /**
   * Initialize the invisible reCAPTCHA after the view exists — the verifier
   * binds to the #mfa-recaptcha-container element in the template.
   */
  ngAfterViewInit(): void {
    if (!isPlatformBrowser(this.platformId) || !this.resolver) {
      return;
    }
    this.initializeRecaptcha();
  }

  /**
   * Clean up the reCAPTCHA verifier on destroy.
   */
  ngOnDestroy(): void {
    if (this.recaptchaVerifier) {
      try {
        // Firebase RecaptchaVerifier has a clear() method for cleanup, but the
        // ApplicationVerifier interface doesn't expose it in the type definitions.
        // The concrete RecaptchaVerifier class does have this method according to
        // Firebase documentation: https://firebase.google.com/docs/auth/web/phone-auth
        // Using optional chaining with type assertion to safely call it.
        (this.recaptchaVerifier as any).clear?.();
        this.recaptchaVerifier = null;
      } catch (error) {
        this.loggingService.logError('Failed to clean up reCAPTCHA', error);
      }
    }
  }

  /**
   * Selects the factor to verify with and resets any per-factor state.
   * @param hint - One of resolver.hints
   */
  selectHint(hint: MultiFactorInfo): void {
    this.selectedHint = hint;
    this.smsSent = false;
    this.verificationId = '';
    this.errorMessage = null;
    this.infoMessage = null;
    this.codeControl.reset('');
  }

  /**
   * Sends the SMS verification code for the selected phone factor using the
   * resolver session and the invisible reCAPTCHA verifier.
   */
  async sendSmsCode(): Promise<void> {
    if (!this.resolver || !this.selectedHint) {
      return;
    }
    if (!this.recaptchaVerifier) {
      this.errorMessage =
        'Security check is not ready yet. Please try again in a moment.';
      return;
    }

    this.loading = true;
    this.errorMessage = null;
    this.infoMessage = null;

    try {
      const auth = this.firebaseHelper.getAuth();
      const phoneAuthProvider = new PhoneAuthProvider(auth);
      this.verificationId = await phoneAuthProvider.verifyPhoneNumber(
        {
          multiFactorHint: this.selectedHint,
          session: this.resolver.session,
        },
        this.recaptchaVerifier,
      );
      this.smsSent = true;
      this.infoMessage = 'Verification code sent to your phone.';
    } catch (error: any) {
      this.loggingService.logError('MFA challenge: SMS send failed', {
        code: error?.code,
      });
      this.errorMessage = this.describeChallengeError(error);
    } finally {
      this.loading = false;
    }
  }

  /**
   * Completes the sign-in by resolving the challenge with the entered code —
   * a phone assertion for SMS factors or a TOTP assertion for authenticator
   * factors — then hands off to the normal post-login flow.
   */
  async submitCode(): Promise<void> {
    if (!this.resolver || !this.selectedHint) {
      return;
    }
    if (this.codeControl.invalid) {
      this.codeControl.markAsTouched();
      return;
    }

    const code = this.codeControl.value.trim();
    this.loading = true;
    this.errorMessage = null;
    this.infoMessage = null;

    try {
      if (this.selectedHint.factorId === this.phoneFactorId) {
        if (!this.verificationId) {
          this.errorMessage = 'Please request a verification code first.';
          return;
        }
        const credential = PhoneAuthProvider.credential(
          this.verificationId,
          code,
        );
        const assertion = PhoneMultiFactorGenerator.assertion(credential);
        await this.resolver.resolveSignIn(assertion);
      } else {
        const assertion = TotpMultiFactorGenerator.assertionForSignIn(
          this.selectedHint.uid,
          code,
        );
        await this.resolver.resolveSignIn(assertion);
      }

      // Success: the auth state listener hydrates the user; hand off to the
      // wait page which completes company/user loading and routes onward.
      this.authService.clearMfaResolver();
      await this.router.navigate(['/logging-in'], { replaceUrl: true });
    } catch (error: any) {
      this.loggingService.logError('MFA challenge: verification failed', {
        code: error?.code,
        factorId: this.selectedHint?.factorId,
      });
      this.errorMessage = this.describeChallengeError(error);
    } finally {
      this.loading = false;
    }
  }

  /**
   * Abandons the challenge: clears the stashed resolver and returns to login.
   */
  async backToLogin(): Promise<void> {
    this.authService.clearMfaResolver();
    await this.router.navigate(['/login'], { replaceUrl: true });
  }

  /**
   * Human-readable label for a factor hint (display name, phone number, or a
   * generic factor description).
   * @param hint - Enrolled factor hint from the resolver
   */
  hintLabel(hint: MultiFactorInfo): string {
    if (hint.displayName) {
      return hint.displayName;
    }
    const phoneNumber = (hint as { phoneNumber?: string }).phoneNumber;
    if (hint.factorId === this.phoneFactorId) {
      return phoneNumber ? `Text message to ${phoneNumber}` : 'Text message (SMS)';
    }
    return 'Authenticator app';
  }

  /**
   * Creates the invisible reCAPTCHA verifier used for SMS challenges.
   */
  private initializeRecaptcha(): void {
    try {
      const auth = this.firebaseHelper.getAuth();
      this.recaptchaVerifier = new RecaptchaVerifier(
        auth,
        'mfa-recaptcha-container',
        {
          size: 'invisible',
          callback: () => {
            // reCAPTCHA solved
          },
          'expired-callback': () => {
            this.errorMessage = 'reCAPTCHA expired. Please try again.';
          },
        },
      );
    } catch (error) {
      this.loggingService.logError('Failed to initialize reCAPTCHA', error);
    }
  }

  /**
   * Maps Firebase challenge errors to friendly, non-leaky inline messages.
   * @param error - Raw Firebase error
   */
  private describeChallengeError(error: any): string {
    switch (error?.code) {
      case 'auth/invalid-verification-code':
        return 'That code is incorrect. Please check it and try again.';
      case 'auth/code-expired':
        return 'That code has expired. Please request a new one.';
      case 'auth/totp-challenge-timeout':
        return 'The verification session timed out. Please sign in again.';
      case 'auth/too-many-requests':
        return 'Too many attempts. Please wait a moment and try again.';
      default:
        return 'Verification failed. Please try again.';
    }
  }
}
